import { Sparkles } from 'lucide-react';
import { Slot } from 'radix-ui';
import { forwardRef, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Spinner } from './Spinner';
import { Tooltip } from './Tooltip';

/* ------------------------------------------------------------------------------------------------
 * Button
 * ---------------------------------------------------------------------------------------------- */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
/** xs 26px · sm 28px · compact 32px · md 36px (default) · lg 44px (tester CTA). */
export type ButtonSize = 'xs' | 'sm' | 'compact' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, sets aria-busy/aria-disabled and ignores clicks (focus is kept). */
  loading?: boolean;
  /** Text shown while loading, e.g. "Saving…" (defaults to the children). */
  loadingText?: ReactNode;
  /** Leading icon (Lucide element). */
  icon?: ReactNode;
  /** Trailing icon (Lucide element). */
  iconEnd?: ReactNode;
  fullWidth?: boolean;
  /** Render the child element (e.g. a router <Link>) with button styles. */
  asChild?: boolean;
}

interface VariantStyle {
  base: string;
  hover: string;
  disabled: string;
  loading: string;
  extra?: string;
}

const BUTTON_VARIANTS: Record<ButtonVariant, VariantStyle> = {
  primary: {
    base: 'border-transparent bg-primary-solid text-white',
    hover: 'hover:bg-primary-solid-hover',
    disabled: 'border-transparent bg-surface-3 text-text-3',
    loading: 'border-transparent bg-primary-solid text-white opacity-85',
  },
  secondary: {
    base: 'border-border bg-surface text-text',
    hover: 'hover:bg-surface-2',
    disabled: 'border-border-2 bg-surface text-text-3',
    loading: 'border-border bg-surface text-text-2',
  },
  ghost: {
    base: 'border-transparent bg-transparent text-text',
    hover: 'hover:bg-surface-2',
    disabled: 'border-transparent bg-transparent text-text-3',
    loading: 'border-transparent bg-transparent text-text-2',
  },
  danger: {
    base: 'border-transparent bg-error text-white',
    hover: 'hover:bg-error-text',
    disabled: 'border-transparent bg-surface-3 text-text-3',
    loading: 'border-transparent bg-error text-white opacity-85',
    extra: 'focus-visible:shadow-[0_0_0_2px_var(--surface),0_0_0_4px_var(--error)]',
  },
};

const BUTTON_SIZES: Record<ButtonSize, { root: string; icon: string; spinner: number }> = {
  xs: { root: 'h-[26px] gap-1.5 rounded-[6px] px-2.5 text-12', icon: '[&>svg]:size-3.5', spinner: 12 },
  sm: { root: 'h-7 gap-1.5 rounded-[6px] px-2.5 text-13', icon: '[&>svg]:size-3.5', spinner: 13 },
  compact: { root: 'h-8 gap-1.5 rounded-[6px] px-3 text-13', icon: '[&>svg]:size-3.5', spinner: 14 },
  md: { root: 'h-9 gap-1.5 rounded-md px-3.5 text-14', icon: '[&>svg]:size-4', spinner: 14 },
  lg: { root: 'h-11 gap-2 rounded-md px-5 text-15 font-semibold', icon: '[&>svg]:size-4', spinner: 16 },
};

export function buttonClassName({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
} = {}): string {
  const v = BUTTON_VARIANTS[variant];
  const s = BUTTON_SIZES[size];
  return cn(
    'relative inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap border font-medium leading-none transition-colors duration-150',
    s.root,
    disabled
      ? cn(v.disabled, 'cursor-not-allowed')
      : loading
        ? cn(v.loading, 'cursor-progress')
        : cn(v.base, v.hover),
    v.extra,
    loading && 'gap-2',
    fullWidth && 'w-full',
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    loadingText,
    icon,
    iconEnd,
    fullWidth,
    asChild = false,
    disabled,
    type,
    className,
    children,
    onClick,
    ...rest
  },
  ref,
) {
  const s = BUTTON_SIZES[size];
  const inactive = Boolean(disabled) || loading;
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (inactive) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  const classes = cn(
    buttonClassName({ variant, size, disabled: Boolean(disabled), loading, fullWidth }),
    className,
  );
  const lead = loading ? (
    <Spinner size={s.spinner} />
  ) : icon ? (
    <span data-slot="icon" aria-hidden className={cn('inline-flex', s.icon)}>
      {icon}
    </span>
  ) : null;
  const trail =
    iconEnd && !loading ? (
      <span data-slot="icon" aria-hidden className={cn('inline-flex', s.icon)}>
        {iconEnd}
      </span>
    ) : null;

  if (asChild) {
    return (
      <Slot.Root
        ref={ref}
        className={classes}
        aria-disabled={inactive || undefined}
        aria-busy={loading || undefined}
        onClick={handleClick}
        {...rest}
      >
        {lead}
        <Slot.Slottable>{children}</Slot.Slottable>
        {trail}
      </Slot.Root>
    );
  }

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled}
      aria-disabled={loading || undefined}
      aria-busy={loading || undefined}
      className={classes}
      onClick={handleClick}
      {...rest}
    >
      {lead}
      {loading && loadingText !== undefined ? loadingText : children}
      {trail}
    </button>
  );
});

/* ------------------------------------------------------------------------------------------------
 * IconButton
 * ---------------------------------------------------------------------------------------------- */

/** secondary (bordered) · ghost (text-2) · subtle (text-3, e.g. table kebabs) · primary · danger. */
export type IconButtonVariant = 'secondary' | 'ghost' | 'subtle' | 'primary' | 'danger';
/** xs 24px · sm 28px · md 36px (default) · lg 44px. */
export type IconButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Required accessible name — icon-only buttons have no visible text. */
  'aria-label': string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Toggle state (`aria-pressed`): primary border + icon colour. */
  pressed?: boolean;
  /** Icon size override in px (defaults: xs/sm 14 · md 16 · lg 18). */
  iconSize?: 12 | 14 | 16 | 18;
  loading?: boolean;
  /** Optional tooltip content (usually the same text as the label). */
  tooltip?: ReactNode;
  /** Keyboard shortcut shown inside the tooltip, e.g. "⌘⇧S". */
  shortcut?: string;
}

const ICON_BUTTON_VARIANTS: Record<IconButtonVariant, { base: string; hover: string; disabled: string }> = {
  secondary: {
    base: 'border-border bg-surface text-text-2',
    hover: 'hover:bg-surface-2 hover:text-text',
    disabled: 'border-border-2 bg-surface text-text-3',
  },
  ghost: {
    base: 'border-transparent bg-transparent text-text-2',
    hover: 'hover:bg-surface-2 hover:text-text',
    disabled: 'border-transparent bg-transparent text-text-3',
  },
  subtle: {
    base: 'border-transparent bg-transparent text-text-3',
    hover: 'hover:bg-surface-2 hover:text-text group-hover/row:text-text-2',
    disabled: 'border-transparent bg-transparent text-text-3 opacity-60',
  },
  primary: {
    base: 'border-transparent bg-primary-solid text-white',
    hover: 'hover:bg-primary-solid-hover',
    disabled: 'border-transparent bg-surface-3 text-text-3',
  },
  danger: {
    base: 'border-transparent bg-transparent text-error-text',
    hover: 'hover:bg-error-soft',
    disabled: 'border-transparent bg-transparent text-text-3',
  },
};

const ICON_SIZE_CLASS = {
  12: '[&>svg]:size-3',
  14: '[&>svg]:size-3.5',
  16: '[&>svg]:size-4',
  18: '[&>svg]:size-[18px]',
} as const;

const ICON_BUTTON_SIZES: Record<IconButtonSize, { root: string; icon: string; spinner: number }> = {
  xs: { root: 'size-6 rounded-[6px]', icon: '[&>svg]:size-3.5', spinner: 12 },
  sm: { root: 'size-7 rounded-[6px]', icon: '[&>svg]:size-3.5', spinner: 13 },
  md: { root: 'size-9 rounded-md', icon: '[&>svg]:size-4', spinner: 14 },
  lg: { root: 'size-11 rounded-md', icon: '[&>svg]:size-[18px]', spinner: 16 },
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    variant = 'secondary',
    size = 'md',
    pressed,
    iconSize,
    loading = false,
    tooltip,
    shortcut,
    disabled,
    type,
    className,
    children,
    onClick,
    ...rest
  },
  ref,
) {
  const v = ICON_BUTTON_VARIANTS[variant];
  const s = ICON_BUTTON_SIZES[size];
  const inactive = Boolean(disabled) || loading;
  const button = (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled}
      aria-pressed={pressed}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      className={cn(
        'inline-grid shrink-0 place-items-center border transition-colors duration-150',
        s.root,
        disabled
          ? cn(v.disabled, 'cursor-not-allowed')
          : pressed
            ? 'border-primary bg-surface text-primary'
            : cn(v.base, !loading && v.hover),
        className,
      )}
      onClick={(event) => {
        if (inactive) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...rest}
    >
      {loading ? (
        <Spinner size={s.spinner} />
      ) : (
        <span aria-hidden className={cn('inline-flex', iconSize ? ICON_SIZE_CLASS[iconSize] : s.icon)}>
          {children}
        </span>
      )}
    </button>
  );
  if (tooltip === undefined && shortcut === undefined) return button;
  return (
    <Tooltip content={tooltip ?? rest['aria-label']} shortcut={shortcut}>
      {button}
    </Tooltip>
  );
});

/* ------------------------------------------------------------------------------------------------
 * AiButton — "AI action" affordances (sparkles icon)
 * ---------------------------------------------------------------------------------------------- */

/**
 * - `outline` — "AI action" button (surface, border, primary sparkles). 36px.
 * - `soft`    — toolbar chip "Improve my report" (primary-soft). 28px.
 * - `dashed`  — "Suggest missing steps" (dashed primary border). 32px.
 * - `chip`    — inline pill "Improve wording". 22px.
 */
export type AiButtonVariant = 'outline' | 'soft' | 'dashed' | 'chip';

export interface AiButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: AiButtonVariant;
  loading?: boolean;
  /** Replaces the sparkles icon. */
  icon?: ReactNode;
}

const AI_VARIANTS: Record<AiButtonVariant, { root: string; hover: string; icon: string; spinner: number }> = {
  outline: {
    root: 'h-9 gap-1.5 rounded-md border border-border bg-surface px-3 text-14 text-text',
    hover: 'hover:bg-surface-2',
    icon: 'text-primary [&>svg]:size-4',
    spinner: 14,
  },
  soft: {
    root: 'h-7 gap-1 rounded-[6px] border border-transparent bg-primary-soft px-2 text-12 text-primary-text',
    hover: 'hover:bg-[color-mix(in_srgb,var(--primary-soft),var(--primary)_10%)]',
    icon: '[&>svg]:size-3',
    spinner: 12,
  },
  dashed: {
    root: 'h-8 gap-1.5 rounded-[6px] border border-dashed border-primary bg-surface px-3 text-13 text-primary-text',
    hover: 'hover:bg-primary-soft',
    icon: '[&>svg]:size-3.5',
    spinner: 14,
  },
  chip: {
    root: 'h-[22px] gap-1 rounded-full border border-transparent bg-primary-soft px-2 text-11 text-primary-text',
    hover: 'hover:bg-[color-mix(in_srgb,var(--primary-soft),var(--primary)_10%)]',
    icon: '[&>svg]:size-[11px]',
    spinner: 11,
  },
};

export const AiButton = forwardRef<HTMLButtonElement, AiButtonProps>(function AiButton(
  { variant = 'outline', loading = false, icon, disabled, type, className, children, onClick, ...rest },
  ref,
) {
  const v = AI_VARIANTS[variant];
  const inactive = Boolean(disabled) || loading;
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap font-medium leading-none transition-colors duration-150',
        v.root,
        disabled ? 'cursor-not-allowed opacity-60' : !loading && v.hover,
        className,
      )}
      onClick={(event) => {
        if (inactive) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...rest}
    >
      {loading ? (
        <Spinner size={v.spinner} className="text-primary" />
      ) : (
        <span aria-hidden className={cn('inline-flex', v.icon)}>
          {icon ?? <Sparkles strokeWidth={2} />}
        </span>
      )}
      {children}
    </button>
  );
});
